/**
 * Tiny relative-time formatter used by contact list / sessions / runs.
 * Wraps the platform `Intl.RelativeTimeFormat` so locale-aware strings
 * land for free.
 *
 * Lifted to a tiny helper so the dashboard doesn't pull `date-fns`
 * just for "2 hours ago" labels.
 */

const RTF_CACHE = new Map<string, Intl.RelativeTimeFormat>();

function rtf(locale: string): Intl.RelativeTimeFormat {
  let cached = RTF_CACHE.get(locale);
  if (!cached) {
    cached = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    RTF_CACHE.set(locale, cached);
  }
  return cached;
}

const DIVISIONS: ReadonlyArray<{ amount: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { amount: 60, unit: "second" },
  { amount: 60, unit: "minute" },
  { amount: 24, unit: "hour" },
  { amount: 7, unit: "day" },
  { amount: 4.34524, unit: "week" },
  { amount: 12, unit: "month" },
  { amount: Number.POSITIVE_INFINITY, unit: "year" },
];

export function relativeTime(input: Date | string | null | undefined, locale = "en"): string {
  if (!input) return "—";
  const date = typeof input === "string" ? new Date(input) : input;
  let duration = (date.getTime() - Date.now()) / 1000;
  for (const div of DIVISIONS) {
    if (Math.abs(duration) < div.amount) {
      return rtf(locale).format(Math.round(duration), div.unit);
    }
    duration /= div.amount;
  }
  return rtf(locale).format(Math.round(duration), "year");
}
